import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { Analytics, CategorySB, ChampionshipSB } from '@mwl/core-lib';
import { extendAnalyticsData, handleEventWithAnalytics } from '@mwl/core-lib';
import ArrowLeftIcon from '@public/assets/common/icons/arrowLeft.svg';
import CloseIcon from '@public/assets/common/icons/closeSmall.svg';
import type { ButtonProps } from '@/components/Button/Button';
import { Button } from '@/components/Button/Button';
import { ButtonIcon } from '@/components/ButtonIcon/ButtonIcon';
import { FormFieldSearch } from '@/components/FormFieldSearch/FormFieldSearch';
import { Loader } from '@/components/Loader/Loader';
import { PerfectScrollbarWithShadow } from '@/components/PerfectScrollbarWithShadow/PerfectScrollbarWithShadow';
import type { PopupClasses } from '@/components/Popup/Popup';
import { Popup } from '@/components/Popup/Popup';
import { Spoiler } from '@/components/Spoiler/Spoiler';
import { FilterCheckbox } from '../FilterCheckbox/FilterCheckbox';
import { filterBackAnalytics, filterPopupButtonsAnalytics, getFilterPopupAnalytics } from './FilterPopup.analytics';
import type { FilterPopupItem, FilterPopupProps, FilterTypes } from './FilterPopup.types';
import styles from './FilterPopup.module.scss';
const popupClasses: PopupClasses = {
  wrapper: styles.popupWrapper,
  container: styles.popupContainer
};
const chipProps: ButtonProps = {
  as: 'external',
  variant: 'filled',
  color: 'black-light',
  className: styles.chip,
  align: 'left',
  cut: false,
  uppercase: false
};
export const FilterPopup = <T extends FilterTypes,>({
  type,
  show,
  onClose,
  defaultSelected,
  items,
  isLoading,
  onSubmit
}: FilterPopupProps<T>) => {
  const {
    t
  } = useTranslation('sport', {
    keyPrefix: 'filters'
  });
  const [searchValue, setSearchValue] = useState('');
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const isCategories = type === 'category';
  const isChampionships = type === 'championship';
  const clearAllText = t('clear_all', 'Clear all');
  const chooseAllText = t('choose_all', 'Choose all');
  const applyText = t('apply', 'Apply filter');
  const isAllSelected = !!items.length && items.length === selectedIds.length;
  const buttonsAnalytics = filterPopupButtonsAnalytics[type];
  const handleChooseAll = () => {
    setSelectedIds(items.map(item => item.id));
  };
  const handleClearAll = () => {
    setSelectedIds([]);
  };
  const handleSelectItem = (item: FilterPopupItem) => {
    setSelectedIds(prevIds => prevIds.includes(item.id) ? prevIds.filter(prevItem => prevItem !== item.id) : [...prevIds, item.id]);
  };
  const getAnalyticsData = useCallback((filterItem: FilterPopupItem): Analytics<'check' | 'uncheck'> => {
    const {
      filterPopupAnalytics
    } = getFilterPopupAnalytics({
      filterType: type,
      filterItem,
      filterItems: items
    });
    return filterPopupAnalytics;
  }, [items, type]);
  useEffect(() => {
    setSelectedIds(defaultSelected);
  }, [defaultSelected, show]);
  return <Popup radius="none" withCloseButton={false} show={show} classes={popupClasses} onClose={onClose} data-sentry-element="Popup" data-sentry-component="FilterPopup" data-sentry-source-file="FilterPopup.tsx">
      <div className={styles.root}>
        <div role="presentation" className={styles.back} onClick={handleEventWithAnalytics(onClose, filterBackAnalytics.click?.eventName, {
        cls: styles.back
      })}>
          <ArrowLeftIcon width={20} height={20} data-sentry-element="ArrowLeftIcon" data-sentry-source-file="FilterPopup.tsx" />
          <span className={styles.title}>{t('title', 'Filters')}</span>
        </div>

        <div className={styles.header}>
          <span className={styles.subtitle}>
            {isCategories && t('category', 'Countries')}
            {isChampionships && t('championships', 'Tournaments')}
          </span>
          <span className={styles.itemCounter}>{items.length}</span>
        </div>

        <div className={styles.fieldWrapper}>
          <FormFieldSearch value={searchValue} onChange={setSearchValue} shouldHandleTransliteration={false} data-sentry-element="FormFieldSearch" data-sentry-source-file="FilterPopup.tsx" />
        </div>

        <div className={styles.chooseActions}>
          <Button className={styles.chooseAll} as="button" variant="filled" color="white" rounded fontWeight="bold" disabled={isAllSelected} onClick={handleChooseAll} analytics={extendAnalyticsData(buttonsAnalytics.chooseAll, {
          text: chooseAllText
        })} data-sentry-element="Button" data-sentry-source-file="FilterPopup.tsx">
            {chooseAllText}
          </Button>
          <Button className={styles.clearAll} as="button" variant="text" color="grey-light" uppercase={false} onClick={handleClearAll} analytics={extendAnalyticsData(buttonsAnalytics.clearAll, {
          text: clearAllText
        })} data-sentry-element="Button" data-sentry-source-file="FilterPopup.tsx">
            {clearAllText}
          </Button>
        </div>

        {isLoading && <div className={styles.loader}>
            <Loader />
          </div>}

        <PerfectScrollbarWithShadow isSwitchOffOnMobile={false} data-sentry-element="PerfectScrollbarWithShadow" data-sentry-source-file="FilterPopup.tsx">
          {!!selectedIds.length && <Spoiler className={styles.spoiler} maxHeight={100}>
              <div className={styles.chips}>
                {isAllSelected && <Button {...chipProps}>
                    {isCategories && t('all_selected.categories', 'All countries selected')}
                    {isChampionships && t('all_selected.championships', 'All tournaments selected')}
                    <ButtonIcon as="button" size="xs" onClick={handleClearAll} color="grey-light" aria-label="filter close">
                      <CloseIcon width={16} height={16} />
                    </ButtonIcon>
                  </Button>}
                {!isAllSelected && selectedIds.map(id => {
              const chip = items.find(item => item.id === id);
              return chip ? <Button key={chip.id} {...chipProps}>
                        {chip.title}
                        <ButtonIcon as="button" size="xs" onClick={handleEventWithAnalytics(() => handleSelectItem(chip), buttonsAnalytics.chip?.click?.eventName, {
                  text: chip.title,
                  cls: styles.chip
                })} aria-label="filter chip close">
                          <CloseIcon width={16} height={16} />
                        </ButtonIcon>
                      </Button> : null;
            })}
              </div>
            </Spoiler>}

          <div className={styles.list}>
            {items.filter(item => !searchValue || item.title?.toLowerCase().includes(searchValue.toLowerCase())).map(item => <FilterCheckbox type={type} key={item.id} isActive={selectedIds.includes(item.id)} item={(item as T extends 'category' ? CategorySB : ChampionshipSB)} onClick={() => handleSelectItem(item)} analytics={getAnalyticsData(item)} />)}
          </div>
        </PerfectScrollbarWithShadow>

        <Button as="button" variant="filled" rounded fullWidth color="red" size="lg" fontSize="xl" onClick={() => onSubmit(selectedIds)} fontWeight="bold" className={styles.apply} analytics={extendAnalyticsData(buttonsAnalytics.apply, {
        text: applyText
      })} data-sentry-element="Button" data-sentry-source-file="FilterPopup.tsx">
          {applyText}
        </Button>
      </div>
    </Popup>;
};